/** Session row absent or cross-org — treat as discarded for ordering UX. */
export class OrderingSessionGoneError extends Error {
  constructor(message = 'Ordering session not found') {
    super(message)
    this.name = 'OrderingSessionGoneError'
  }
}

/** Restart rejected (409) because the patient already has an open draft; resume it instead. */
export class RestartDraftExistsError extends Error {
  readonly existingDraftSessionId: string

  constructor(existingDraftSessionId: string) {
    super('Patient already has an open draft')
    this.name = 'RestartDraftExistsError'
    this.existingDraftSessionId = existingDraftSessionId
  }
}

/** Restart rejected (409) because server-side eligibility changed after it was read. */
export class RestartConflictError extends Error {
  constructor(message = 'Order restart eligibility changed') {
    super(message)
    this.name = 'RestartConflictError'
  }
}

export type OrderingSessionDiscardConflictKind = 'already_submitted' | 'submit_in_flight'

/** DELETE rejected because submit finished or is in progress. */
export class OrderingSessionDiscardConflictError extends Error {
  readonly kind: OrderingSessionDiscardConflictKind

  constructor(kind: OrderingSessionDiscardConflictKind, message: string) {
    super(message)
    this.name = 'OrderingSessionDiscardConflictError'
    this.kind = kind
  }
}

export function parseDiscardConflictKind(detail: string): OrderingSessionDiscardConflictKind {
  const lower = detail.toLowerCase()
  if (lower.includes('being submitted') || lower.includes('submit in progress')) {
    return 'submit_in_flight'
  }
  if (
    lower.includes('cancel order') ||
    lower.includes('already submitted') ||
    lower.includes('imdb_order') ||
    lower.includes('submitted order')
  ) {
    return 'already_submitted'
  }
  return 'already_submitted'
}

/** Submit rejected (409) because the session already produced an order. */
export class OrderingSessionAlreadySubmittedError extends Error {
  constructor(message = 'This order has already been submitted.') {
    super(message)
    this.name = 'OrderingSessionAlreadySubmittedError'
  }
}

export const DISCARD_CONFLICT_MESSAGES: Record<OrderingSessionDiscardConflictKind, string> = {
  already_submitted:
    'This order was already submitted and cannot be discarded. Use Cancel Order instead if you need to stop it.',
  submit_in_flight:
    'This order is being submitted right now. Wait for submission to finish, then try again if you still need to discard.',
}
